<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  detectVariables,
  detectTemplateMode,
  validatePositionalSequence,
} from '../helpers/templateVariables.js';
import Button from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
});

const TEMPLATE_LANGUAGES = [
  { value: 'pt_BR', label: 'Português (BR)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'es_ES', label: 'Español (ES)' },
  { value: 'es_MX', label: 'Español (MX)' },
  { value: 'fr_FR', label: 'Français (FR)' },
  { value: 'de_DE', label: 'Deutsch (DE)' },
  { value: 'it_IT', label: 'Italiano (IT)' },
];

const TEMPLATE_CATEGORIES = [
  { value: 'UTILITY', label: 'UTILITY' },
  { value: 'MARKETING', label: 'MARKETING' },
  { value: 'AUTHENTICATION', label: 'AUTHENTICATION' },
];

const TEMPLATE_NAME_REGEX = /^[a-z0-9_]{1,512}$/;

const { t } = useI18n();
const store = useStore();

const wrapToken = token => `{{${token}}}`;

const formDialogRef = ref(null);
const deleteDialogRef = ref(null);

const editingTemplate = ref(null);
const deletingTemplate = ref(null);
const isSubmitting = ref(false);

const form = reactive({
  name: '',
  language: 'pt_BR',
  category: 'UTILITY',
  bodyText: '',
  headerText: '',
  footerText: '',
  showHeader: false,
  showFooter: false,
  showButtons: false,
  buttons: [],
});

const formErrors = reactive({
  name: '',
  body: '',
  category: '',
  language: '',
});

const variableExamples = reactive({ body: {}, header: {}, buttons: {} });

const templates = computed(
  () => store.getters['whatsappTemplates/getTemplates']
);
const uiFlags = computed(() => store.getters['whatsappTemplates/getUIFlags']);

const detectedBodyVars = computed(() => detectVariables(form.bodyText));
const detectedHeaderVar = computed(() => detectVariables(form.headerText));
const detectedButtonUrlVars = computed(() =>
  form.buttons.map(btn => {
    if (btn.type !== 'URL') return null;
    const result = detectVariables(btn.url || '');
    return result.tokens.length > 0 ? result : null;
  })
);
const templateMode = computed(() =>
  detectTemplateMode({
    bodyText: form.bodyText,
    headerText: form.showHeader ? form.headerText : '',
    buttons: form.showButtons ? form.buttons : [],
  })
);
const VAR_TOKEN_RE = /\{\{[^}]+\}\}/;

const variableWarnings = computed(() => {
  const warnings = [];
  const W = key => t(`INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLES.${key}`);

  // Bug B: invalid tokens (uppercase, special chars) — use dedicated key instead of raw helper string
  const hasInvalidBody = detectedBodyVars.value.errors.some(e =>
    e.includes('Invalid variable token')
  );
  const hasInvalidHeader =
    form.showHeader &&
    detectedHeaderVar.value.errors.some(e =>
      e.includes('Invalid variable token')
    );
  if (hasInvalidBody || hasInvalidHeader) warnings.push(W('INVALID_TOKEN'));

  if (templateMode.value.mode === 'mixed') warnings.push(W('MODE_MIXED'));

  if (detectedBodyVars.value.mode === 'positional') {
    const seq = validatePositionalSequence(detectedBodyVars.value.tokens);
    if (!seq.valid) warnings.push(W('SEQUENCE_INVALID'));
  }
  if (form.showHeader && detectedHeaderVar.value.tokens.length > 1)
    warnings.push(W('HEADER_MAX_ONE'));

  detectedButtonUrlVars.value.forEach((result, idx) => {
    if (result && result.mode === 'named') {
      warnings.push(`${W('BUTTON_URL_POSITIONAL_ONLY')} (botão ${idx + 1})`);
    }
  });

  // Bug C: FOOTER não suporta variáveis
  if (form.showFooter && VAR_TOKEN_RE.test(form.footerText))
    warnings.push(W('FOOTER_NO_VARIABLES'));

  // Bug D: BODY/HEADER não podem começar nem terminar com variável
  const bodyTrimmed = form.bodyText.trim();
  if (
    bodyTrimmed &&
    (bodyTrimmed.startsWith('{{') || bodyTrimmed.endsWith('}}'))
  ) {
    warnings.push(W('BODY_EDGE_VARIABLE'));
  }
  if (form.showHeader) {
    const headerTrimmed = form.headerText.trim();
    if (
      headerTrimmed &&
      (headerTrimmed.startsWith('{{') || headerTrimmed.endsWith('}}'))
    ) {
      warnings.push(W('HEADER_EDGE_VARIABLE'));
    }
  }

  return warnings;
});

const isFormValid = computed(() => {
  if (!form.name.trim() || !TEMPLATE_NAME_REGEX.test(form.name)) return false;
  if (!form.bodyText.trim()) return false;
  if (variableWarnings.value.length > 0) return false;
  const bodyExamplesOk = detectedBodyVars.value.tokens.every(token =>
    variableExamples.body[token]?.trim()
  );
  if (!bodyExamplesOk) return false;
  if (form.showHeader) {
    const headerExamplesOk = detectedHeaderVar.value.tokens.every(token =>
      variableExamples.header[token]?.trim()
    );
    if (!headerExamplesOk) return false;
  }
  if (form.showButtons) {
    const btnExamplesOk = detectedButtonUrlVars.value.every(
      (result, idx) =>
        !result ||
        result.tokens.length === 0 ||
        variableExamples.buttons[String(idx)]?.trim()
    );
    if (!btnExamplesOk) return false;
  }
  return true;
});

const statusBadgeClass = status => {
  const map = {
    APPROVED: 'bg-n-teal-3 text-n-teal-11',
    PENDING: 'bg-n-amber-3 text-n-amber-11',
    REJECTED: 'bg-n-ruby-3 text-n-ruby-11',
    PAUSED: 'bg-n-slate-3 text-n-slate-11',
    DISABLED: 'bg-n-slate-3 text-n-slate-11',
  };
  return map[status] || 'bg-n-slate-3 text-n-slate-11';
};

const statusLabel = status => {
  const key = `INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.${status}`;
  return key;
};

const resetForm = () => {
  form.name = '';
  form.language = 'pt_BR';
  form.category = 'UTILITY';
  form.bodyText = '';
  form.headerText = '';
  form.footerText = '';
  form.showHeader = false;
  form.showFooter = false;
  form.showButtons = false;
  form.buttons = [];
  formErrors.name = '';
  formErrors.body = '';
  formErrors.category = '';
  formErrors.language = '';
  Object.keys(variableExamples.body).forEach(
    k => delete variableExamples.body[k]
  );
  Object.keys(variableExamples.header).forEach(
    k => delete variableExamples.header[k]
  );
  Object.keys(variableExamples.buttons).forEach(
    k => delete variableExamples.buttons[k]
  );
};

const openCreateDialog = () => {
  editingTemplate.value = null;
  resetForm();
  formDialogRef.value?.open();
};

const openEditDialog = template => {
  editingTemplate.value = template;
  form.name = template.name;
  form.language = template.language;
  form.category = template.category;

  const body = template.components?.find(c => c.type === 'BODY');
  const header = template.components?.find(c => c.type === 'HEADER');
  const footer = template.components?.find(c => c.type === 'FOOTER');
  const buttons = template.components?.find(c => c.type === 'BUTTONS');

  form.bodyText = body?.text || '';
  form.headerText = header?.text || '';
  form.footerText = footer?.text || '';
  form.showHeader = !!header;
  form.showFooter = !!footer;
  form.showButtons = !!buttons;
  form.buttons =
    buttons?.buttons?.map(b => ({
      type: b.type,
      text: b.text,
      url: b.url || '',
    })) || [];

  formErrors.name = '';
  formErrors.body = '';
  formErrors.category = '';
  formErrors.language = '';

  // Populate variableExamples from existing template data
  Object.keys(variableExamples.body).forEach(
    k => delete variableExamples.body[k]
  );
  Object.keys(variableExamples.header).forEach(
    k => delete variableExamples.header[k]
  );
  Object.keys(variableExamples.buttons).forEach(
    k => delete variableExamples.buttons[k]
  );

  if (body?.example) {
    const bodyText = body.example.body_text?.[0]; // positional: array of values
    const namedParams = body.example.body_text_named_params; // named: [{param_name, example}]
    if (Array.isArray(bodyText)) {
      bodyText.forEach((val, i) => {
        variableExamples.body[String(i + 1)] = val;
      });
    } else if (Array.isArray(namedParams)) {
      namedParams.forEach(p => {
        variableExamples.body[p.param_name] = p.example;
      });
    }
  }
  if (header?.example) {
    const headerText = header.example.header_text?.[0]; // positional: single string
    const namedParams = header.example.header_text_named_params;
    if (typeof headerText === 'string') {
      variableExamples.header['1'] = headerText;
    } else if (Array.isArray(namedParams)) {
      namedParams.forEach(p => {
        variableExamples.header[p.param_name] = p.example;
      });
    }
  }
  if (buttons?.buttons) {
    buttons.buttons.forEach((btn, idx) => {
      if (btn.type === 'URL' && btn.example?.[0]) {
        variableExamples.buttons[String(idx)] = btn.example[0];
      }
    });
  }

  formDialogRef.value?.open();
};

const openDeleteDialog = template => {
  deletingTemplate.value = template;
  deleteDialogRef.value?.open();
};

const addButton = () => {
  if (form.buttons.length >= 3) return;
  form.buttons.push({ type: 'QUICK_REPLY', text: '', url: '' });
};

const removeButton = index => {
  form.buttons.splice(index, 1);
};

const validateForm = () => {
  let valid = true;
  formErrors.name = '';
  formErrors.body = '';

  if (!form.name.trim()) {
    formErrors.name = 'INBOX_MGMT.WHATSAPP_TEMPLATES.VALIDATION.NAME_REQUIRED';
    valid = false;
  } else if (!TEMPLATE_NAME_REGEX.test(form.name)) {
    formErrors.name = 'INBOX_MGMT.WHATSAPP_TEMPLATES.VALIDATION.NAME_INVALID';
    valid = false;
  }

  if (!form.bodyText.trim()) {
    formErrors.body = 'INBOX_MGMT.WHATSAPP_TEMPLATES.VALIDATION.BODY_REQUIRED';
    valid = false;
  }

  // --- Variable validations (checks 1-8) ---
  const mode = templateMode.value.mode;

  // 1. Mixed mode
  if (mode === 'mixed') {
    valid = false;
  }

  // 2. Invalid tokens in BODY or HEADER
  if (
    detectedBodyVars.value.errors.length > 0 ||
    (form.showHeader && detectedHeaderVar.value.errors.length > 0)
  ) {
    valid = false;
  }

  // 3. Positional sequence in BODY
  if (detectedBodyVars.value.mode === 'positional') {
    const seq = validatePositionalSequence(detectedBodyVars.value.tokens);
    if (!seq.valid) valid = false;
  }

  // 4 & 5. HEADER checks (only when header is active)
  if (form.showHeader && detectedHeaderVar.value.tokens.length > 0) {
    // Max 1 variable
    if (detectedHeaderVar.value.tokens.length > 1) {
      valid = false;
    }
    // If positional, must be exactly {{1}}
    if (
      detectedHeaderVar.value.mode === 'positional' &&
      !detectedHeaderVar.value.tokens.includes('1')
    ) {
      valid = false;
    }
  }

  // 6. URL button with named variable (only when buttons active)
  if (form.showButtons) {
    detectedButtonUrlVars.value.forEach(result => {
      if (result && result.mode === 'named') {
        valid = false;
      }
    });
  }

  // 7. Examples required for every detected token
  detectedBodyVars.value.tokens.forEach(token => {
    if (!variableExamples.body[token]?.trim()) {
      valid = false;
    }
  });
  if (form.showHeader) {
    detectedHeaderVar.value.tokens.forEach(token => {
      if (!variableExamples.header[token]?.trim()) {
        valid = false;
      }
    });
  }
  if (form.showButtons) {
    detectedButtonUrlVars.value.forEach((result, idx) => {
      if (result && result.tokens.length > 0) {
        if (!variableExamples.buttons[String(idx)]?.trim()) {
          valid = false;
        }
      }
    });
  }

  return valid;
};

const buildComponents = () => {
  const components = [];

  // HEADER
  if (form.showHeader && form.headerText.trim()) {
    const headerComp = {
      type: 'HEADER',
      format: 'TEXT',
      text: form.headerText.trim(),
    };
    const hVars = detectedHeaderVar.value;
    if (hVars.mode === 'positional' && hVars.tokens.length) {
      headerComp.example = {
        header_text: [variableExamples.header['1'] || ''],
      };
    } else if (hVars.mode === 'named' && hVars.tokens.length) {
      headerComp.example = {
        header_text_named_params: hVars.tokens.map(tok => ({
          param_name: tok,
          example: variableExamples.header[tok] || '',
        })),
      };
    }
    components.push(headerComp);
  }

  // BODY
  const bodyComp = { type: 'BODY', text: form.bodyText.trim() };
  const bVars = detectedBodyVars.value;
  if (bVars.mode === 'positional' && bVars.tokens.length) {
    const ordered = [...new Set(bVars.tokens)].sort(
      (a, b) => Number(a) - Number(b)
    );
    bodyComp.example = {
      body_text: [ordered.map(tok => variableExamples.body[tok] || '')],
    };
  } else if (bVars.mode === 'named' && bVars.tokens.length) {
    const unique = [...new Set(bVars.tokens)];
    bodyComp.example = {
      body_text_named_params: unique.map(tok => ({
        param_name: tok,
        example: variableExamples.body[tok] || '',
      })),
    };
  }
  components.push(bodyComp);

  // FOOTER — Meta não suporta variáveis em footer
  if (form.showFooter && form.footerText.trim()) {
    components.push({ type: 'FOOTER', text: form.footerText.trim() });
  }

  // BUTTONS
  if (form.showButtons && form.buttons.length > 0) {
    const buttons = form.buttons
      .filter(b => b.text.trim())
      .map((b, idx) => {
        if (b.type === 'URL') {
          const btnObj = {
            type: 'URL',
            text: b.text.trim(),
            url: b.url.trim(),
          };
          const urlVars = detectedButtonUrlVars.value[idx];
          if (urlVars && urlVars.mode === 'positional') {
            btnObj.example = [variableExamples.buttons[String(idx)] || ''];
          }
          return btnObj;
        }
        return { type: 'QUICK_REPLY', text: b.text.trim() };
      });
    if (buttons.length > 0) {
      components.push({ type: 'BUTTONS', buttons });
    }
  }

  return components;
};

const handleSave = async () => {
  if (!validateForm()) return;

  isSubmitting.value = true;
  const components = buildComponents();

  try {
    if (editingTemplate.value) {
      await store.dispatch('whatsappTemplates/update', {
        inboxId: props.inboxId,
        id: editingTemplate.value.id,
        template: { category: form.category, components },
      });
      useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('whatsappTemplates/create', {
        inboxId: props.inboxId,
        template: {
          name: form.name,
          language: form.language,
          category: form.category,
          components,
        },
      });
      useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.CREATE_SUCCESS'));
    }
    formDialogRef.value?.close();
  } catch {
    const key = editingTemplate.value
      ? 'INBOX_MGMT.WHATSAPP_TEMPLATES.API.UPDATE_ERROR'
      : 'INBOX_MGMT.WHATSAPP_TEMPLATES.API.CREATE_ERROR';
    useAlert(t(key));
  } finally {
    isSubmitting.value = false;
  }
};

const handleDelete = async () => {
  if (!deletingTemplate.value) return;
  try {
    await store.dispatch('whatsappTemplates/delete', {
      inboxId: props.inboxId,
      name: deletingTemplate.value.name,
      hsmId: deletingTemplate.value.id,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.DELETE_SUCCESS'));
    deleteDialogRef.value?.close();
  } catch {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.DELETE_ERROR'));
  }
};

const handleSync = async () => {
  try {
    await store.dispatch('whatsappTemplates/sync', { inboxId: props.inboxId });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.SYNC_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.SYNC_ERROR'));
  }
};

onMounted(() => {
  store.dispatch('whatsappTemplates/fetch', { inboxId: props.inboxId });
});
</script>

<template>
  <div class="py-6 space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-0.5">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <Button
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-refresh-cw"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC_BUTTON')"
          :is-loading="uiFlags.isSyncing"
          @click="handleSync"
        />
        <Button
          variant="solid"
          color="blue"
          size="sm"
          icon="i-lucide-plus"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW_BUTTON')"
          @click="openCreateDialog"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="uiFlags.isFetching" class="flex justify-center py-10">
      <SpinnerLoader :size="28" class="text-n-blue-9" />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!templates.length"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <span class="i-lucide-layout-template w-12 h-12 text-n-slate-8 mb-3" />
      <p class="text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11 mt-1 max-w-sm">
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_DESCRIPTION') }}
      </p>
    </div>

    <!-- Table -->
    <div v-else class="border border-n-weak rounded-xl overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-n-surface-2">
          <tr>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.NAME') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.CATEGORY') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.LANGUAGE') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.STATUS') }}
            </th>
            <th class="px-4 py-3 text-right font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="template in templates"
            :key="template.id || template.name"
            class="hover:bg-n-surface-1 transition-colors"
          >
            <td class="px-4 py-3 font-mono text-xs text-n-slate-12">
              {{ template.name }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ template.category }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ template.language }}
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="statusBadgeClass(template.status)"
              >
                {{ $t(statusLabel(template.status)) }}
              </span>
            </td>
            <td class="px-4 py-3">
              <div class="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  color="slate"
                  size="xs"
                  icon="i-lucide-pencil"
                  @click="openEditDialog(template)"
                />
                <Button
                  variant="ghost"
                  color="slate"
                  size="xs"
                  icon="i-lucide-trash-2"
                  @click="openDeleteDialog(template)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Form dialog (create / edit) -->
    <Dialog
      ref="formDialogRef"
      :title="
        editingTemplate
          ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TITLE_EDIT')
          : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TITLE_CREATE')
      "
      :confirm-button-label="
        $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SAVE_BUTTON')
      "
      :cancel-button-label="
        $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CANCEL_BUTTON')
      "
      :disable-confirm-button="!isFormValid"
      :is-loading="isSubmitting"
      width="lg"
      @confirm="handleSave"
    >
      <div class="space-y-4">
        <!-- Variable warnings banner — Fase 2 -->
        <div
          v-if="variableWarnings.length"
          class="rounded-lg bg-n-ruby-3 text-n-ruby-11 p-3 space-y-1 text-sm"
        >
          <p v-for="(w, wi) in variableWarnings" :key="wi">{{ w }}</p>
        </div>

        <!-- Name (disabled in edit mode) -->
        <label class="block">
          <span class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_LABEL') }} *
          </span>
          <input
            v-model="form.name"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_PLACEHOLDER')
            "
            :disabled="!!editingTemplate"
            class="w-full px-3 py-2.5 text-sm border rounded-lg bg-n-surface-1 focus:outline-none focus:border-n-brand transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :class="
              formErrors.name
                ? 'border-n-ruby-9'
                : 'border-n-weak hover:border-n-slate-6'
            "
          />
          <p class="mt-1 text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_HINT') }}
          </p>
          <p v-if="formErrors.name" class="mt-1 text-xs text-n-ruby-11">
            {{ $t(formErrors.name) }}
          </p>
        </label>

        <!-- Language + Category row -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <span class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE_LABEL') }}
            </span>
            <SelectInput
              v-model="form.language"
              :options="TEMPLATE_LANGUAGES"
              :disabled="!!editingTemplate"
            />
          </div>
          <div>
            <span class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY_LABEL') }}
            </span>
            <SelectInput
              v-model="form.category"
              :options="TEMPLATE_CATEGORIES"
            />
          </div>
        </div>

        <!-- Components section -->
        <div class="space-y-3 pt-2 border-t border-n-weak">
          <span class="block text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.COMPONENTS_TITLE') }}
          </span>

          <!-- Header (optional) -->
          <div v-if="form.showHeader" class="space-y-1">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_LABEL') }}
              </span>
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.REMOVE')"
                @click="
                  form.showHeader = false;
                  form.headerText = '';
                "
              />
            </div>
            <input
              v-model="form.headerText"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_PLACEHOLDER')
              "
              class="w-full px-3 py-2.5 text-sm border border-n-weak rounded-lg bg-n-surface-1 hover:border-n-slate-6 focus:outline-none focus:border-n-brand transition-colors"
            />
            <!-- HEADER variable examples -->
            <div
              v-if="detectedHeaderVar.tokens.length"
              class="mt-2 space-y-1.5"
            >
              <span class="block text-xs font-medium text-n-slate-11">
                {{
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLES.EXAMPLES_LABEL')
                }}
              </span>
              <div
                v-for="token in detectedHeaderVar.tokens"
                :key="'hvar-' + token"
                class="flex items-center gap-2"
              >
                <span class="font-mono text-xs text-n-slate-10 w-28 shrink-0">{{
                  wrapToken(token)
                }}</span>
                <input
                  v-model="variableExamples.header[token]"
                  type="text"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLES.EXAMPLE_PLACEHOLDER'
                    )
                  "
                  class="flex-1 px-2.5 py-1.5 text-sm border border-n-weak rounded-lg bg-n-surface-1 focus:outline-none focus:border-n-brand transition-colors"
                />
              </div>
            </div>
          </div>

          <!-- Body (required) -->
          <label class="block">
            <span class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_LABEL') }}
            </span>
            <textarea
              v-model="form.bodyText"
              rows="4"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_PLACEHOLDER')
              "
              class="w-full px-3 py-2.5 text-sm border rounded-lg bg-n-surface-1 hover:border-n-slate-6 focus:outline-none focus:border-n-brand transition-colors resize-none"
              :class="formErrors.body ? 'border-n-ruby-9' : 'border-n-weak'"
            />
            <p v-if="formErrors.body" class="mt-1 text-xs text-n-ruby-11">
              {{ $t(formErrors.body) }}
            </p>
            <!-- BODY variable examples -->
            <div v-if="detectedBodyVars.tokens.length" class="mt-2 space-y-1.5">
              <span class="block text-xs font-medium text-n-slate-11">
                {{
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLES.EXAMPLES_LABEL')
                }}
              </span>
              <div
                v-for="token in detectedBodyVars.tokens"
                :key="'bvar-' + token"
                class="flex items-center gap-2"
              >
                <span class="font-mono text-xs text-n-slate-10 w-28 shrink-0">{{
                  wrapToken(token)
                }}</span>
                <input
                  v-model="variableExamples.body[token]"
                  type="text"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLES.EXAMPLE_PLACEHOLDER'
                    )
                  "
                  class="flex-1 px-2.5 py-1.5 text-sm border border-n-weak rounded-lg bg-n-surface-1 focus:outline-none focus:border-n-brand transition-colors"
                />
              </div>
            </div>
          </label>

          <!-- Footer (optional) -->
          <div v-if="form.showFooter" class="space-y-1">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER_LABEL') }}
              </span>
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.REMOVE')"
                @click="
                  form.showFooter = false;
                  form.footerText = '';
                "
              />
            </div>
            <input
              v-model="form.footerText"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER_PLACEHOLDER')
              "
              class="w-full px-3 py-2.5 text-sm border border-n-weak rounded-lg bg-n-surface-1 hover:border-n-slate-6 focus:outline-none focus:border-n-brand transition-colors"
            />
          </div>

          <!-- Buttons section (optional) -->
          <div v-if="form.showButtons" class="space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_SECTION_TITLE')
                }}
              </span>
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.REMOVE')"
                @click="
                  form.showButtons = false;
                  form.buttons = [];
                "
              />
            </div>
            <div
              v-for="(btn, idx) in form.buttons"
              :key="idx"
              class="flex items-start gap-2 p-3 border border-n-weak rounded-lg bg-n-surface-1"
            >
              <div class="flex-1 grid grid-cols-2 gap-2">
                <div>
                  <span class="block text-xs text-n-slate-11 mb-1">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TYPE_LABEL')
                    }}
                  </span>
                  <SelectInput
                    v-model="btn.type"
                    :options="[
                      {
                        value: 'QUICK_REPLY',
                        label: $t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TYPE_QUICK_REPLY'
                        ),
                      },
                      {
                        value: 'URL',
                        label: $t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TYPE_URL'
                        ),
                      },
                    ]"
                  />
                </div>
                <div>
                  <span class="block text-xs text-n-slate-11 mb-1">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TEXT_LABEL')
                    }}
                  </span>
                  <input
                    v-model="btn.text"
                    type="text"
                    class="w-full px-2.5 py-2 text-sm border border-n-weak rounded-lg bg-n-surface-1 focus:outline-none focus:border-n-brand transition-colors"
                  />
                </div>
                <div v-if="btn.type === 'URL'" class="col-span-2">
                  <span class="block text-xs text-n-slate-11 mb-1">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_URL_LABEL')
                    }}
                  </span>
                  <input
                    v-model="btn.url"
                    type="text"
                    class="w-full px-2.5 py-2 text-sm border border-n-weak rounded-lg bg-n-surface-1 focus:outline-none focus:border-n-brand transition-colors"
                  />
                  <!-- URL button variable example -->
                  <div
                    v-if="detectedButtonUrlVars[idx]"
                    class="mt-1.5 flex items-center gap-2"
                  >
                    <span
                      class="font-mono text-xs text-n-slate-10 w-28 shrink-0"
                    >
                      {{ wrapToken('1') }}
                    </span>
                    <input
                      v-model="variableExamples.buttons[String(idx)]"
                      type="text"
                      :placeholder="
                        $t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLES.EXAMPLE_PLACEHOLDER'
                        )
                      "
                      class="flex-1 px-2.5 py-1.5 text-sm border border-n-weak rounded-lg bg-n-surface-1 focus:outline-none focus:border-n-brand transition-colors"
                    />
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                icon="i-lucide-x"
                class="mt-5"
                @click="removeButton(idx)"
              />
            </div>
            <Button
              v-if="form.buttons.length < 3"
              variant="outline"
              color="slate"
              size="sm"
              icon="i-lucide-plus"
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ADD_BUTTON')"
              @click="addButton"
            />
          </div>

          <!-- Add optional components -->
          <div class="flex flex-wrap gap-2 pt-1">
            <Button
              v-if="!form.showHeader"
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-layout-template"
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ADD_HEADER')"
              @click="form.showHeader = true"
            />
            <Button
              v-if="!form.showFooter"
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-minus-square"
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ADD_FOOTER')"
              @click="form.showFooter = true"
            />
            <Button
              v-if="!form.showButtons"
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-mouse-pointer-click"
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ADD_BUTTONS')"
              @click="
                form.showButtons = true;
                form.buttons = [{ type: 'QUICK_REPLY', text: '', url: '' }];
              "
            />
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Delete confirmation dialog -->
    <Dialog
      ref="deleteDialogRef"
      :title="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE.TITLE')"
      :confirm-button-label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE.CONFIRM')"
      :cancel-button-label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE.CANCEL')"
      @confirm="handleDelete"
    >
      <p class="text-sm text-n-slate-11">
        {{
          $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE.MESSAGE', {
            name: deletingTemplate?.name,
          })
        }}
      </p>
    </Dialog>
  </div>
</template>
